/**
 * Format rules on many columns at once, and the standard presets (owner,
 * 2026-10-06). Pure — the Format tab is a thin shell over this.
 *
 * **Copied, never linked.** A rule set applied to six columns is six copies,
 * each living in its column's `format` exactly as a hand-made rule does. That
 * is the whole storage argument: `ScreenerColumn.format` already saves (qv-data
 * keeps it as JSON on `screener_column`), syncs to the screen row, publishes
 * with a group's screen and is evaluated by `cellStyleFor` — so a copy needs no
 * contract field, no migration and no second evaluation path. A screen-level
 * shared rule set would need all four.
 *
 * What a link would buy — "edit once, update everywhere" — is recovered without
 * one: rules are plain data, so the columns carrying a rule are found by
 * **equality** (`columnsWithRule`) and rewritten together when the user asks.
 * No stored id means nothing to keep in step when a column is renamed, hidden
 * or dropped by the upstream engine.
 */

import type {
  ScreenerColumn,
  ScreenerFilterCondition,
  ScreenerFormatRule,
} from "@qufolio/model";

/** How rules land on a column that already has some. */
export type ApplyMode = "replace" | "add";

/**
 * Two rules are the same rule when their `when` and `style` say the same
 * thing. Compared structurally with keys sorted, so `{op, value}` and
 * `{value, op}` match — the order a hand edit wrote them in is not meaning.
 * An absent `bold` and `bold: false` are the same style.
 */
export function rulesEqual(a: ScreenerFormatRule, b: ScreenerFormatRule): boolean {
  return canonical(a) === canonical(b);
}

function canonical(rule: ScreenerFormatRule): string {
  const style = { ...rule.style };
  if (style.bold === false) delete style.bold;
  return stable({ when: rule.when, style });
}

function stable(value: unknown): string {
  if (Array.isArray(value)) return `[${value.map(stable).join(",")}]`;
  if (value && typeof value === "object") {
    const entries = Object.entries(value as Record<string, unknown>)
      .filter(([, v]) => v !== undefined)
      .sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0));
    return `{${entries.map(([k, v]) => `${JSON.stringify(k)}:${stable(v)}`).join(",")}}`;
  }
  return JSON.stringify(value);
}

/**
 * The columns with these rules written onto `fields`, everything else as it
 * was — by identity, so a column the apply did not touch does not re-render.
 *
 * A field with no entry in `columns` yet gets one, appended: the Format tab
 * offers every column in the universe, and a column nobody has configured has
 * no row to hold its rules until now. That is what the old single-column
 * `patch` did, applied per field.
 *
 * `add` skips a rule the column already carries, so applying a preset twice
 * does not stack the same colour twice and leave two rows to delete.
 */
export function applyRules(
  columns: readonly ScreenerColumn[],
  fields: readonly string[],
  rules: readonly ScreenerFormatRule[],
  mode: ApplyMode,
): ScreenerColumn[] {
  const wanted = new Set(fields);
  const written = (existing: readonly ScreenerFormatRule[] | undefined): ScreenerFormatRule[] =>
    mode === "replace"
      ? rules.map((r) => ({ ...r }))
      : [...(existing ?? []), ...rules.filter((r) => !(existing ?? []).some((e) => rulesEqual(e, r)))];

  const out = columns.map((c) => (wanted.has(c.field) ? { ...c, format: written(c.format) } : c));
  const present = new Set(columns.map((c) => c.field));
  for (const field of fields) {
    if (!present.has(field)) out.push({ field, format: written(undefined) });
  }
  return out;
}

/** Every configured column that carries `rule`, by field. */
export function columnsWithRule(columns: readonly ScreenerColumn[], rule: ScreenerFormatRule): string[] {
  return columns.filter((c) => (c.format ?? []).some((r) => rulesEqual(r, rule))).map((c) => c.field);
}

/**
 * Rewrite `from` as `to` on every column carrying it — the "update on all N
 * columns with this rule" half of the equality link. A column that holds the
 * rule twice gets both rewritten, which is the same thing it would have seen
 * edited by hand twice.
 */
export function replaceRuleEverywhere(
  columns: readonly ScreenerColumn[],
  from: ScreenerFormatRule,
  to: ScreenerFormatRule,
): ScreenerColumn[] {
  return columns.map((c) =>
    (c.format ?? []).some((r) => rulesEqual(r, from))
      ? { ...c, format: (c.format ?? []).map((r) => (rulesEqual(r, from) ? { ...to } : r)) }
      : c,
  );
}

/**
 * The standard rule sets. **A fixed list**: saved presets of one's own would
 * need a store, and these cover what a trading screen colours.
 *
 * Every colour is a theme token, so a preset follows a theme switch (D13).
 * There are no tinted backgrounds — `ScreenerCellStyle` carries a token or a
 * hex and nothing that mixes one with the cell's own background, so a "light
 * green" band would have to be a hex that stops following the theme. The
 * bands use bold text in the sign colours instead.
 *
 * `rowField` is only for the one preset that colours a cell by *another*
 * column; the tab passes the column the user is most likely to mean.
 */
export interface FormatPreset {
  id: string;
  label: string;
  title: string;
  rules: (rowField: string) => ScreenerFormatRule[];
}

export const FORMAT_PRESETS: readonly FormatPreset[] = [
  {
    id: "sign",
    label: "Sign colours",
    title: "Above zero in the positive colour, below zero in the negative",
    rules: () => [
      { when: { op: "gt", value: 0 }, style: { fg: "positive" } },
      { when: { op: "lt", value: 0 }, style: { fg: "negative" } },
    ],
  },
  {
    id: "flag",
    label: "Up/Down flag",
    title: "A true/false column: true in the positive colour, false muted",
    rules: () => [
      { when: { op: "eq", value: true }, style: { fg: "positive" } },
      { when: { op: "eq", value: false }, style: { fg: "text.muted" } },
    ],
  },
  {
    id: "threshold",
    label: "Above / below",
    title: "Above one level positive, below another negative — edit the two numbers",
    rules: () => [
      { when: { op: "gt", value: 70 }, style: { fg: "positive" } },
      { when: { op: "lt", value: 30 }, style: { fg: "negative" } },
    ],
  },
  {
    id: "rank",
    label: "Rank ≥ 90",
    title: "A 0–100 rank: 90 and over highlighted, 99 and over in the accent",
    rules: () => [
      { when: { op: "gte", value: 90 }, style: { bg: "selection", bold: true } },
      { when: { op: "gte", value: 99 }, style: { bg: "accent", fg: "background", bold: true } },
    ],
  },
  {
    id: "bands",
    label: "% change bands",
    title: "Moves of 5% or more either way in bold sign colours",
    rules: () => [
      { when: { op: "gte", value: 5 }, style: { fg: "positive", bold: true } },
      { when: { op: "lte", value: -5 }, style: { fg: "negative", bold: true } },
    ],
  },
  {
    id: "blank",
    label: "Blank dimmed",
    title: "An empty cell in the muted colour",
    rules: () => [{ when: { op: "blank" }, style: { fg: "text.muted" } }],
  },
  {
    id: "row",
    label: "Row from another column",
    title: "Colour these cells by another column's value — pick the column in the rule",
    rules: (rowField) => [
      {
        when: { field: rowField, op: "gt", value: 0 } as ScreenerFilterCondition,
        style: { fg: "positive" },
      },
      {
        when: { field: rowField, op: "lt", value: 0 } as ScreenerFilterCondition,
        style: { fg: "negative" },
      },
    ],
  },
];

/**
 * The theme tokens the colour selects offer, in the order a person reaches for
 * them: the sign pair first, then the quiet ones, then the accents. Not every
 * token in the theme — chrome tokens like `border` and `grid` are chosen to be
 * ignorable, and a cell drawn in one is invisible.
 */
export const FORMAT_TOKENS: readonly { token: string; label: string }[] = [
  { token: "positive", label: "Positive" },
  { token: "negative", label: "Negative" },
  { token: "neutral", label: "Neutral" },
  { token: "text.muted", label: "Muted" },
  { token: "text", label: "Text" },
  { token: "background", label: "Background" },
  { token: "selection", label: "Highlight" },
  { token: "accent", label: "Accent" },
  { token: "warn", label: "Warn" },
  { token: "accent.orange", label: "Orange" },
  { token: "accent.teal", label: "Teal" },
  { token: "accent.purple", label: "Purple" },
  { token: "series1", label: "Series 1" },
  { token: "series2", label: "Series 2" },
  { token: "series3", label: "Series 3" },
];

/**
 * One set of rules and every column carrying exactly it — a line of the Format
 * tab's **Rules in use** (owner, 2026-10-06). Grouped by the whole list, in
 * order, because order is meaning here (the last match wins): two columns with
 * the same two rules swapped are two sets.
 */
export interface RuleSet {
  rules: ScreenerFormatRule[];
  fields: string[];
}

export function ruleSetsInUse(columns: readonly ScreenerColumn[]): RuleSet[] {
  const sets = new Map<string, RuleSet>();
  for (const column of columns) {
    const rules = column.format ?? [];
    if (rules.length === 0) continue;
    const key = rules.map(canonical).join("|");
    const set = sets.get(key);
    if (set) set.fields.push(column.field);
    else sets.set(key, { rules: [...rules], fields: [column.field] });
  }
  return [...sets.values()];
}

/** The columns with `fields`' rules removed — the ✕ on a rule set. */
export function removeRuleSet(columns: readonly ScreenerColumn[], fields: readonly string[]): ScreenerColumn[] {
  const gone = new Set(fields);
  return columns.map((column) => {
    if (!gone.has(column.field) || !column.format) return column;
    const { format: _format, ...rest } = column;
    return rest;
  });
}

/**
 * Whether any rule compares this column's own value as a **number** — what
 * makes it meaningless on a text column like Symbol or Name, so the picker
 * greys those out. A rule reading another column (`field` / `children`) says
 * nothing about this one's type.
 */
export function hasNumberRules(rules: readonly ScreenerFormatRule[]): boolean {
  return rules.some((rule) => {
    const when = rule.when as { op?: string; value?: unknown; field?: unknown; children?: unknown };
    if (when.field !== undefined || when.children !== undefined) return false;
    return ["gt", "gte", "lt", "lte", "between"].includes(when.op ?? "") || typeof when.value === "number";
  });
}
